import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiClient } from '../../lib/api-client';

interface NotifRow {
  id: string;
  event: string;
  title: string;
  body: string | null;
  linkPath: string | null;
  readAt: string | null;
  createdAt: string;
}

interface NotifResponse {
  data: NotifRow[];
  meta: { total: number; unread: number };
}

async function fetchNotifications(): Promise<NotifResponse> {
  const res = await apiClient.get<NotifResponse>('/notifications?page=1&pageSize=10');
  return res.data;
}

export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const query = useQuery({
    queryKey: ['notifications', 'bell'],
    queryFn: fetchNotifications,
    refetchInterval: 15_000,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => apiClient.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'bell'] }),
  });
  const markAllRead = useMutation({
    mutationFn: async () => apiClient.post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'bell'] }),
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const unread = query.data?.meta.unread ?? 0;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-help="notifications-panel"
          className="absolute right-0 z-20 mt-2 w-80 overflow-hidden surface shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <span className="text-sm font-medium text-slate-800">Notifications</span>
            {unread > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="text-xs text-brand hover:underline"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>
          <div className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">
            {(query.data?.data ?? []).map((n) => (
              <div key={n.id} className={`px-4 py-3 ${n.readAt ? 'bg-white' : 'bg-blue-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                    {n.body && <div className="mt-0.5 text-xs text-slate-600">{n.body}</div>}
                    <div className="mt-1 text-[11px] text-slate-400">
                      {new Date(n.createdAt).toLocaleString('fr-FR')}
                    </div>
                  </div>
                  {!n.readAt && (
                    <button
                      onClick={() => markRead.mutate(n.id)}
                      className="ml-2 text-[11px] text-brand hover:underline"
                    >
                      Lue
                    </button>
                  )}
                </div>
                {n.linkPath && (
                  <Link
                    to={n.linkPath.split('?')[0] ?? '/'}
                    onClick={() => setOpen(false)}
                    className="mt-1.5 inline-block text-xs font-medium text-brand hover:underline"
                  >
                    Voir →
                  </Link>
                )}
              </div>
            ))}
            {query.data && query.data.data.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-400">
                Aucune notification.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
